import React, { useEffect, useState, useMemo } from 'react';
import {
  Container, Select, MenuItem, TextField, Button, Table, TableHead, TableRow,
  TableCell, TableBody, Typography, Box,
  TableRowProps,
  styled,
  TableContainer,
  createTheme,
  ThemeProvider,
  SelectChangeEvent
} from '@mui/material';
import {
  startOfMonth, endOfMonth, format, parseISO, addMonths, subMonths
} from 'date-fns';
import NavBar from '../Navigation/Navbar';

interface DepartmentEntry {
  department: string;
  net_sales: number;
}

interface ApiResponse {
  date: string;
  departments: DepartmentEntry[];
}

const locations = [
  'Steeles', 'New-Scarborough', 'Brampton', 'Newmarket', 'Dufferin-Orfus',
  'Vaughan', 'Queensway', 'Niagara', 'Kingston', 'Alliston', 'OPM-Hamilton', 'Pickering',
  'Cartwright', 'Yorkgate',
];

// Custom theme
const theme = createTheme({
  typography: {
    fontFamily: 'Arial, sans-serif', // Set default font to Arial
  },
  components: {
    MuiTableCell: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: '4px 8px',
          whiteSpace: 'nowrap',
          border: '1px solid rgba(224, 224, 224, 1)', // Add borders to cells
          fontFamily: 'Arial, sans-serif', // Ensure Arial font
          textAlign: 'center', // Center-align text
          [theme.breakpoints.down('sm')]: {
            fontSize: '0.75rem',
            padding: '2px 4px',
          },
          [theme.breakpoints.up('sm')]: {
            fontSize: '0.875rem',
            padding: '4px 8px',
          },
        }),
        head: ({ theme }) => ({
          backgroundColor: '#f5f5f5',
          fontWeight: 'bold',
          border: '1px solid rgba(224, 224, 224, 1)',
          whiteSpace: 'nowrap',
          fontFamily: 'Arial, sans-serif', // Ensure Arial font
          textAlign: 'center', // Center-align header text
          [theme.breakpoints.down('sm')]: {
            fontSize: '0.75rem',
            padding: '2px 4px',
          },
          [theme.breakpoints.up('sm')]: {
            fontSize: '0.875rem',
            padding: '4px 8px',
          },
        }),
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: ({ theme }) => ({
          fontFamily: 'Arial, sans-serif', // Ensure Arial font
          textAlign: 'center', // Center-align input text
          [theme.breakpoints.down('sm')]: {
            fontSize: '0.75rem',
          },
          [theme.breakpoints.up('sm')]: {
            fontSize: '0.875rem',
          },
        }),
        input: {
          '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
            '-webkit-appearance': 'none',
            margin: 0,
          },
          '&[type=number]': {
            '-moz-appearance': 'textfield',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          fontFamily: 'Arial, sans-serif', // Ensure Arial font
          textAlign: 'center', // Center-align button text
          [theme.breakpoints.down('sm')]: {
            fontSize: '0.75rem',
          },
          [theme.breakpoints.up('sm')]: {
            fontSize: '0.875rem',
          },
        }),
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: ({ theme }) => ({
          fontFamily: 'Arial, sans-serif', // Ensure Arial font
          textAlign: 'center', // Center-align select text
          [theme.breakpoints.down('sm')]: {
            fontSize: '0.75rem',
          },
          [theme.breakpoints.up('sm')]: {
            fontSize: '0.875rem',
          },
        }),
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: ({ theme }) => ({
          fontFamily: 'Arial, sans-serif', // Ensure Arial font
          textAlign: 'center', // Center-align labels
          [theme.breakpoints.down('sm')]: {
            fontSize: '0.75rem',
          },
          [theme.breakpoints.up('sm')]: {
            fontSize: '0.875rem',
          },
        }),
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          fontFamily: 'Arial, sans-serif', // Ensure Arial font
          textAlign: 'center', // Center-align typography
        },
      },
    },
  },
});

// Styled components
const Root = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  height: '84vh', // Adjust height if necessary
  backgroundColor: '#f0f2f5',
});

const TableContainerStyled = styled(TableContainer)({
  flexGrow: 1,
  overflowX: 'auto',
  overflowY: 'auto',
});

const FiltersContainer = styled('div')({
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  backgroundColor: '#fff',
  padding: '0.3rem',
  borderTop: '1px solid #ccc',
});

const TableRowStyled = styled(TableRow)<TableRowProps>(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  cursor: 'pointer', // Indicate the row is clickable
  '&:hover': {
    backgroundColor: '#ffffe0', // Light yellow on hover
  },
  '&.Mui-selected': {
    backgroundColor: '#ffffe0', // Light yellow when selected
  },
  '&.Mui-selected:hover': {
    backgroundColor: '#ffffe0', // Maintain light yellow on hover when selected
  },
}));

const taxDepartments = [
  'StatusIndianRebate8%',
  'GST',
  'PST',
  'Tax Incl',
  'PST for over $30',
  'HST@13%',
  'Rebate@8%',
  'FED RELIEF -5%',
  'PROV RELIEF -8%',
];


const DepartmentNetSalesView: React.FC = () => {
  const currentMonth = new Date();
  const [location, setLocation] = useState('');
  const [month, setMonth] = useState<string | null>(format(currentMonth, 'yyyy-MM'));
  const [startDate, setStartDate] = useState<string | null>(format(startOfMonth(currentMonth), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState<string | null>(format(endOfMonth(currentMonth), 'yyyy-MM-dd'));
  const [data, setData] = useState<ApiResponse[]>([]);
  const [departmentHeaders, setDepartmentHeaders] = useState<string[]>([]);

  const fetchData = async () => {
    if (!location) return;

    const res = await fetch('/data/getDepartmentNetSales', {
      method: 'POST',
      credentials:'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ location, startDate, endDate, month })
    });

    const result: ApiResponse[] = await res.json();
    setData(result);
    extractDepartments(result);
  };

  const extractDepartments = (data: ApiResponse[]) => {
    const unique = new Set<string>();
    data.forEach(entry => {
      entry.departments.forEach(d => unique.add(d.department || 'Uncategorized'));
    });

    const sorted = Array.from(unique).sort((a, b) => {
      const aIsTax = taxDepartments.includes(a);
      const bIsTax = taxDepartments.includes(b);
      if (aIsTax && !bIsTax) return 1;  // a comes after
      if (!aIsTax && bIsTax) return -1; // b comes after
      return a.localeCompare(b); // normal alphabetical
    });

    setDepartmentHeaders(sorted);
  };


  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, month, startDate, endDate]);

  const handleLocationChange = (event: SelectChangeEvent<string>) => {
    setLocation(event.target.value);
  };

  const handleMonthChange = (event: SelectChangeEvent<string>) => {
    const selectedMonth = event.target.value;
    setMonth(selectedMonth);
    setStartDate(null);
    setEndDate(null);
  };

  const handleStartDateChange = (date: string) => {
    setStartDate(date);
    setMonth(null);
  };

  const handleEndDateChange = (date: string) => {
    setEndDate(date);
    setMonth(null);
  };

  const generateMonthOptions = () => {
    const months = [];
    let date = subMonths(currentMonth, 11);
    for (let i = 0; i < 12; i++) {
      months.push({ label: format(date, 'MMMM yyyy'), value: format(date, 'yyyy-MM') });
      date = addMonths(date, 1);
    }
    return months;
  };

  const formatCurrency = (value: number | null | undefined) =>
    value !== null && value !== undefined
      ? `$${value.toLocaleString('en-US', { minimumFractionDigits: 2 })}`
      : '-';



  const transformedData = useMemo(() => {
    return data.map((entry) => {
      const row: { [key: string]: string | number } = {
        date: format(parseISO(entry.date), 'MMM dd, yyyy'),
      };

      let totalSales = 0;
      let grandTotal = 0;

      departmentHeaders.forEach(dept => {
        const found = entry.departments.find(d => d.department === dept);
        const value = found ? found.net_sales : 0;
        row[dept] = value;

        if (!taxDepartments.includes(dept)) {
          totalSales += value;
        }
        grandTotal += value;
      });

      row['Total Sales'] = totalSales;
      row['Grand Total'] = grandTotal;

      return row;
    });
  }, [data, departmentHeaders]);


  return (

    <>
      <NavBar />
      <ThemeProvider theme={theme}>
        <Root>
          <TableContainerStyled>
            <Table
              stickyHeader
              style={{ tableLayout: 'auto', minWidth: 800 }}
              size="small"
            >
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  {departmentHeaders.map((dept, idx) => (
                    <TableCell key={idx}>{dept}</TableCell>
                  ))}
                  <TableCell>Total Sales</TableCell>
                  <TableCell>Grand Total</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {transformedData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={departmentHeaders.length + 3}>
                      <Typography align="center">No data available</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  transformedData.map((row, idx) => (
                    <TableRowStyled key={idx}>
                      <TableCell>{row.date}</TableCell>
                      {departmentHeaders.map((dept) => (
                        <TableCell key={dept} align="right">
                          {formatCurrency(row[dept] as number)}
                        </TableCell>
                      ))}
                      <TableCell align="right">
                        {formatCurrency(row['Total Sales'] as number)}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(row['Grand Total'] as number)}
                      </TableCell>
                    </TableRowStyled>
                  ))
                )}
              </TableBody>

            </Table>
          </TableContainerStyled>
          <FiltersContainer>
            <Container>
              <Box
                sx={{
                  display: 'flex',
                  gap: '0.3rem',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                }}
              >
                <Select
                  value={location}
                  onChange={handleLocationChange}
                  displayEmpty
                  variant="outlined"
                  size="small"
                  sx={{
                    minWidth: '127px',
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  }}
                >
                  <MenuItem value="" disabled>
                    Select Location
                  </MenuItem>
                  {locations.map((loc) => (
                    <MenuItem key={loc} value={loc}>
                      {loc}
                    </MenuItem>
                  ))}
                </Select>

                <Select
                  value={month || ''}
                  onChange={handleMonthChange}
                  displayEmpty
                  variant="outlined"
                  size="small"
                  sx={{
                    minWidth: '130px',
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  }}
                >
                  <MenuItem value="" disabled>
                    Select Month
                  </MenuItem>
                  {generateMonthOptions().map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>

                <TextField
                  label="Start Date"
                  type="date"
                  value={startDate || ''}
                  onChange={(e) => handleStartDateChange(e.target.value)}
                  InputLabelProps={{
                    shrink: true,
                    sx: { fontSize: { xs: '0.75rem', sm: '0.875rem' } },
                  }}
                  variant="outlined"
                  size="small"
                  margin="dense"
                  sx={{ minWidth: '130px' }}
                  inputProps={{
                    style: { fontSize: '0.75rem' },
                  }}
                />

                <TextField
                  label="End Date"
                  type="date"
                  value={endDate || ''}
                  onChange={(e) => handleEndDateChange(e.target.value)}
                  InputLabelProps={{
                    shrink: true,
                    sx: { fontSize: { xs: '0.75rem', sm: '0.875rem' } },
                  }}
                  variant="outlined"
                  size="small"
                  margin="dense"
                  sx={{ minWidth: '130px' }}
                  inputProps={{
                    style: { fontSize: '0.75rem' },
                  }}
                />

                <Button
                  variant="contained"
                  onClick={fetchData}
                  size="small"
                  sx={{
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  }}
                >
                  Get Data
                </Button>


              </Box>
            </Container>
          </FiltersContainer>
        </Root>
      </ThemeProvider>
    </>
    // </Container>
  );
};

export default DepartmentNetSalesView;
